import * as Localization from 'expo-localization';
import logger from 'features/observability/logger';
import { Dimensions } from 'react-native';

import { NonNullableObject } from '@rbi-ctg/frontend';
import {
  logBrazeCommerceEvent,
  logBrazeCustomEvent,
  requestImmediateBrazeDataFlush,
} from 'state/braze/log-braze-event';
import { breakpoints } from 'styles/constants/breakpoints';
import { appVersion } from 'utils/app-version-info';
import { appStartId, brand, env, regionEnum } from 'utils/environment';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';

import { AllowedEvent } from './allowed-events';
import { CustomEventNames, EventTypes } from './constants';
import { AnalyticsContext, mergeAnalyticsAttributes } from './core/context';
import { AnalyticsDestination } from './core/destination';
import { AnalyticsDispatcher } from './core/dispatcher';
import { AnalyticsQueue } from './core/queue';
import { getScreenContext } from './core/screen-context';
import { trackAmplitudeEvent } from './providers/amplitude/client';
import { IAmplitudeUniversalAttributes, ILogEvent } from './types';
import {
  expandProductAttributes,
  getIsMobileWeb,
  isCommerceEvent,
  isExpandableEvent,
  normalizeBooleans,
  sanitizeValues,
  toAttributesWithValidLengthValues,
} from './utils';

export {
  ButtonClickSource,
  CustomEventNames,
  EventTypes,
  ClickEventComponentNames,
} from './constants';
const universalContext = new AnalyticsContext<
  NonNullableObject<Partial<IAmplitudeUniversalAttributes>>
>(
  normalizeBooleans(
    sanitizeValues({
      'Service Mode': '',
      'Pickup Mode': '',
      'Source Page': getScreenContext().currentScreen,
      isLoyaltyUser: !!syncStorage.getString(SyncStorageKey.USER_ID),
      currentBuild: appVersion,
    })
  )
);

type LogOptions = {
  flushBrazeAfterLog?: boolean;
  skipLoggingToBraze?: boolean;
};

const analyticsQueue = new AnalyticsQueue<AllowedEvent, LogOptions>();

const flushBrazeData = () => {
  try {
    requestImmediateBrazeDataFlush();
  } catch (error) {
    logger.error({ error, message: 'Braze > post-purchase data flush error' });
  }
};

const analyticsDestinations: Array<AnalyticsDestination<AllowedEvent, LogOptions>> = [
  {
    deliver: (event, attributes, logOptions) => {
      if (logOptions?.skipLoggingToBraze) {
        return;
      }

      if (isCommerceEvent(event.name)) {
        const products = attributes.products ?? [];
        logBrazeCommerceEvent(event.name as CustomEventNames, products as any, attributes);
      } else {
        logBrazeCustomEvent(event.name as CustomEventNames, attributes);
      }

      if (logOptions?.flushBrazeAfterLog) {
        flushBrazeData();
      }
    },
  },
  {
    deliver: (event, attributes) => {
      if (isExpandableEvent(event.name as CustomEventNames)) {
        const products = attributes.products ?? [];
        const expandedProducts = expandProductAttributes(products as any);
        trackAmplitudeEvent(event.name, { ...attributes, ...expandedProducts });
        return;
      }

      trackAmplitudeEvent(event.name, attributes);
    },
  },
];

const analyticsDispatcher = new AnalyticsDispatcher(analyticsDestinations);

const getDeviceTime = () => new Date().toTimeString().split(' ')[0];
const getDeviceTimeTimezone = () => {
  const legacyLocalization = Localization as typeof Localization & { timezone?: string | null };
  const nativeTimeZone = Localization.getCalendars?.()[0]?.timeZone ?? legacyLocalization.timezone;

  if (nativeTimeZone) {
    return nativeTimeZone;
  }

  try {
    const intlTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (intlTimeZone) {
      return intlTimeZone;
    }
  } catch (error) {
    logger.debug({ error, message: 'Unable to determine device timezone via Intl' });
  }

  const offsetInMinutes = new Date().getTimezoneOffset();
  const sign = offsetInMinutes > 0 ? '-' : '+';
  const absoluteOffsetInMinutes = Math.abs(offsetInMinutes);
  const hours = String(Math.floor(absoluteOffsetInMinutes / 60)).padStart(2, '0');
  const minutes = String(absoluteOffsetInMinutes % 60).padStart(2, '0');

  return `UTC${sign}${hours}:${minutes}`;
};

export const logEvent: ILogEvent = (
  eventName: CustomEventNames | string,
  eventType: EventTypes,
  attributes?: object,
  customFlags?: object,
  logOptions?: LogOptions
) => {
  logRBIEvent(
    {
      name: eventName as any,
      type: eventType,
      attributes: attributes as Record<string, any>,
      customFlags: customFlags as Record<string, any>,
    },
    logOptions
  );
};

export const updateUniversalAttributes = (
  newAttributes: Partial<IAmplitudeUniversalAttributes>
) => {
  universalContext.set(
    normalizeBooleans(
      sanitizeValues({
        ...universalContext.get(),
        ...newAttributes,
        'Source Page': getScreenContext().currentScreen,
      })
    )
  );
};

export const getUniversalAttributes = () => ({
  ...universalContext.get(),
  'Source Page': getScreenContext().currentScreen,
});

const dispatchRBIEvent = (event: AllowedEvent, logOptions?: LogOptions): void => {
  try {
    const universalAttrs = universalContext.get();
    const { currentScreen, previousScreen } = getScreenContext();
    const globalAttributes: any = {
      brand: brand().toUpperCase(),
      region: regionEnum,
      env: env() as string,
      currentScreen,
      previousScreen,
      deviceTime: getDeviceTime(),
      deviceTimeTimezone: getDeviceTimeTimezone(),
      serviceMode: universalAttrs['Service Mode'],
      pickupMode: universalAttrs['Pickup Mode'],
      appBuild: universalAttrs.currentBuild,
      isMobileWeb: getIsMobileWeb(),
      isSmallScreen: Dimensions.get('window').width < breakpoints.md,
      appStartId,
      'UTM Source': universalAttrs['UTM Source'],
      utmSource: universalAttrs['UTM Source'],
      'UTM Medium': universalAttrs['UTM Medium'],
      utmMedium: universalAttrs['UTM Medium'],
      'UTM Campaign': universalAttrs['UTM Campaign'],
      utmCampaign: universalAttrs['UTM Campaign'],
      'UTM Term': universalAttrs['UTM Term'],
      utmTerm: universalAttrs['UTM Term'],
      'UTM Content': universalAttrs['UTM Content'],
      utmContent: universalAttrs['UTM Content'],
      selectedStoreId: universalAttrs.selectedStoreId,
      posVendor: universalAttrs.posVendor,
    };

    const attributes = toAttributesWithValidLengthValues(
      mergeAnalyticsAttributes(globalAttributes, event.globalAttributes, event.attributes)
    );

    analyticsDispatcher.dispatch(event, attributes, logOptions);
  } catch (error) {
    logger.error({ message: 'Error logging event to Amplitude', error });
  }
};

export const logRBIEvent = (event: AllowedEvent, logOptions?: LogOptions): void => {
  analyticsQueue.submit(event, logOptions, dispatchRBIEvent);
};

export const flushEventQueue = () => analyticsQueue.enable(dispatchRBIEvent);
